import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, CreditCard, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { isDemoMode } from './DemoBanner';
import DemoUpgradePrompt from './DemoUpgradePrompt';
import OrgScopedBillingView from './billing/OrgScopedBillingView';
import CurrentPlanCard from './billing/CurrentPlanCard';
import UsageCard from './billing/UsageCard';
import SubscribeCard from './billing/SubscribeCard';
import ConfirmModifyDialog from './billing/ConfirmModifyDialog';
import InvoicesTable from './billing/InvoicesTable';
import PaymentHistory from './billing/PaymentHistory';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Billing tab orchestrator. Owns subscription/usage/transactions state,
 * Stripe checkout + modify flows, polling, and composes the visual pieces.
 */
export default function BillingTab() {
  const { user, activeOrgId, userOrgs } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [pollingStatus, setPollingStatus] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [modifyLoading, setModifyLoading] = useState(false);
  const [addonsOpen, setAddonsOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [orgBilling, setOrgBilling] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [showDemoPrompt, setShowDemoPrompt] = useState(false);

  const isProvider = user?.account_type === 'provider';
  const isInOrgView = isProvider && !!activeOrgId;
  const activeOrg = isInOrgView ? userOrgs.find(o => o.org_id === activeOrgId) : null;

  // Add-on state for plan configurator (used by both Subscribe and Modify flows)
  const [extraOrgs, setExtraOrgs] = useState(0);
  const [extraCompanies, setExtraCompanies] = useState(0);
  const [extraAdmins, setExtraAdmins] = useState(0);
  const [extraUsers, setExtraUsers] = useState(0);

  const DEMO_EMAILS = ['demo@allaboutclaims.com', 'demo-provider@allaboutclaims.com'];
  const isDemo = isDemoMode() && DEMO_EMAILS.includes(user?.email);

  const fetchAll = useCallback(async () => {
    if (isInOrgView) {
      try {
        const res = await axios.get(`${API}/billing/org/${activeOrgId}`, { withCredentials: true });
        setOrgBilling(res.data);
      } catch { setOrgBilling(null); }
      setLoading(false);
      return;
    }
    try {
      const [subRes, usageRes, txRes, invRes] = await Promise.all([
        axios.get(`${API}/billing/subscription`, { withCredentials: true }),
        axios.get(`${API}/billing/usage`, { withCredentials: true }),
        axios.get(`${API}/billing/transactions`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/billing/invoices`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setSubscription(subRes.data);
      setUsage(usageRes.data);
      setTransactions(txRes.data);
      setInvoices(invRes.data);
    } catch {
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Hydrate add-on counters from active subscription or current usage (trialing)
  useEffect(() => {
    if (subscription?.status === 'active' && subscription?.addons) {
      const a = subscription.addons;
      setExtraOrgs(a.extra_orgs || 0);
      setExtraCompanies(a.extra_companies || 0);
      setExtraAdmins(a.extra_admins || 0);
      setExtraUsers(a.extra_users || 0);
    } else if (subscription?.status === 'trialing' && usage) {
      const baseCompanies = isProvider ? 6 : 1;
      const baseAdmins = 1;
      const baseUsers = 2;
      const baseOrgs = isProvider ? 3 : 0;
      setExtraCompanies(Math.max(0, (usage.companies?.current || 0) - baseCompanies));
      setExtraAdmins(Math.max(0, (usage.admins?.current || 0) - baseAdmins));
      setExtraUsers(Math.max(0, (usage.users?.current || 0) - baseUsers));
      if (isProvider) setExtraOrgs(Math.max(0, (usage.organizations?.current || 0) - baseOrgs));
    }
  }, [subscription, usage, isProvider]);

  // Live price estimate
  useEffect(() => {
    const calc = async () => {
      try {
        const body = isProvider
          ? { plan_type: 'provider', extra_orgs: extraOrgs, extra_companies: extraCompanies, extra_admins: extraAdmins, extra_users: extraUsers }
          : { plan_type: 'solo', extra_companies: extraCompanies, extra_admins: extraAdmins, extra_users: extraUsers };
        const res = await axios.post(`${API}/billing/calculate`, body, { withCredentials: true });
        setEstimate(res.data);
      } catch (err) { console.warn('[BillingTab] price calc failed', err); }
    };
    calc();
  }, [extraOrgs, extraCompanies, extraAdmins, extraUsers, isProvider]);

  // Poll checkout status after returning from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) return;
    let attempts = 0;
    const maxAttempts = 5;
    setPollingStatus('checking');

    const poll = async () => {
      if (attempts >= maxAttempts) { setPollingStatus('timeout'); return; }
      try {
        const res = await axios.get(`${API}/billing/checkout/status/${sessionId}`, { withCredentials: true });
        if (res.data.payment_status === 'paid') {
          setPollingStatus('success');
          toast.success('Payment successful! Your plan is now active.');
          setSearchParams({});
          fetchAll();
          return;
        } else if (res.data.status === 'expired') {
          setPollingStatus('expired');
          return;
        }
      } catch (err) { console.warn('[BillingTab] checkout poll attempt failed', err); }
      attempts++;
      setTimeout(poll, 2000);
    };
    poll();
  }, [searchParams, setSearchParams, fetchAll]);

  useEffect(() => {
    if (searchParams.get('canceled') === 'true') {
      toast.info('Checkout was canceled.');
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // ── Stripe handlers ─────────────────────────────────────────────────────
  const handleCheckout = async () => {
    if (isDemo) { setShowDemoPrompt(true); return; }
    setCheckoutLoading(true);
    try {
      const body = {
        origin_url: window.location.origin,
        plan_type: isProvider ? 'provider' : 'solo',
        extra_companies: extraCompanies,
        extra_admins: extraAdmins,
        extra_users: extraUsers,
      };
      if (isProvider) body.extra_orgs = extraOrgs;
      const res = await axios.post(`${API}/billing/checkout`, body, { withCredentials: true });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      if (status === 401) toast.error('Session expired. Please log in again.');
      else toast.error(detail || `Checkout failed (${status || 'network error'})`);
      console.error('Checkout error:', status, err.response?.data || err.message);
    } finally { setCheckoutLoading(false); }
  };

  const handleModify = async () => {
    setModifyLoading(true);
    try {
      const body = { extra_companies: extraCompanies, extra_admins: extraAdmins, extra_users: extraUsers };
      if (isProvider) body.extra_orgs = extraOrgs;
      await axios.post(`${API}/billing/modify`, body, { withCredentials: true });
      toast.success('Plan updated! Prorated changes applied to your next invoice.');
      setConfirmOpen(false);
      setAddonsOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update plan');
    } finally { setModifyLoading(false); }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel? Your subscription will remain active until the end of the current billing period.')) return;
    setCancelLoading(true);
    try {
      await axios.post(`${API}/billing/cancel`, {}, { withCredentials: true });
      toast.success('Subscription will cancel at end of billing period');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel subscription');
    } finally { setCancelLoading(false); }
  };

  const handlePortal = async () => {
    setPortalLoading(true);
    try {
      const res = await axios.post(`${API}/billing/portal`, { return_url: window.location.href }, { withCredentials: true });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to open billing portal');
    } finally { setPortalLoading(false); }
  };

  // ── Derived state for child components ──────────────────────────────────
  const getAddonChanges = () => {
    const currentAddons = subscription?.addons || {};
    const prices = isProvider
      ? { orgs: 30, companies: 9, admins: 7, users: 5 }
      : { orgs: 0, companies: 29, admins: 11, users: 9 };
    const items = [
      { label: 'Extra Organizations', current: currentAddons.extra_orgs || 0, proposed: extraOrgs, price: prices.orgs, show: isProvider },
      { label: 'Extra Companies', current: currentAddons.extra_companies || 0, proposed: extraCompanies, price: prices.companies, show: true },
      { label: 'Extra Admins', current: currentAddons.extra_admins || 0, proposed: extraAdmins, price: prices.admins, show: true },
      { label: 'Extra Users', current: currentAddons.extra_users || 0, proposed: extraUsers, price: prices.users, show: true },
    ];
    return items
      .filter(item => item.show)
      .map(item => {
        const diff = item.proposed - item.current;
        return diff !== 0 ? { label: item.label, diff, costDiff: diff * item.price } : null;
      })
      .filter(Boolean);
  };

  const hasChanges = () => {
    const a = subscription?.addons || {};
    if (isProvider && extraOrgs !== (a.extra_orgs || 0)) return true;
    if (extraCompanies !== (a.extra_companies || 0)) return true;
    if (extraAdmins !== (a.extra_admins || 0)) return true;
    if (extraUsers !== (a.extra_users || 0)) return true;
    return false;
  };

  // ── Render ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading billing...
      </div>
    );
  }

  if (isInOrgView && orgBilling) {
    return <OrgScopedBillingView activeOrg={activeOrg} orgBilling={orgBilling} />;
  }
  if (isInOrgView && !orgBilling?.linked) {
    return (
      <div className="text-center py-12">
        <CreditCard className="w-10 h-10 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">This organization does not have a linked tenant. Billing is managed at the provider level.</p>
      </div>
    );
  }

  const sub = subscription || {};
  const trial = sub.trial_info || {};
  const stripeDetails = sub.stripe_details || {};
  const isTrialing = sub.status === 'trialing';
  const isActive = sub.status === 'active';
  const isExpired = sub.status === 'trial_expired';
  const isCanceling = sub.cancel_at_period_end || stripeDetails.cancel_at_period_end;
  const planLabel = isProvider ? 'Provider' : 'Solo';
  const basePrice = isProvider ? '$99.00' : '$39.00';

  return (
    <div className="space-y-6" data-testid="billing-tab">
      {pollingStatus === 'checking' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center gap-2 text-sm text-blue-700" data-testid="payment-polling">
          <Loader2 className="w-4 h-4 animate-spin" /> Verifying your payment...
        </div>
      )}
      {pollingStatus === 'success' && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex items-center gap-2 text-sm text-green-700" data-testid="payment-success">
          <CheckCircle2 className="w-4 h-4" /> Payment confirmed! Your subscription is active.
        </div>
      )}

      <CurrentPlanCard
        sub={sub} trial={trial} stripeDetails={stripeDetails}
        isTrialing={isTrialing} isActive={isActive} isExpired={isExpired} isCanceling={isCanceling}
        planLabel={planLabel} portalLoading={portalLoading} cancelLoading={cancelLoading}
        handlePortal={handlePortal} handleCancel={handleCancel}
      />

      <UsageCard usage={usage} isProvider={isProvider} />

      {(isTrialing || isExpired || isActive) && (
        <SubscribeCard
          sub={sub} isActive={isActive} isProvider={isProvider}
          planLabel={planLabel} basePrice={basePrice}
          extraOrgs={extraOrgs} setExtraOrgs={setExtraOrgs}
          extraCompanies={extraCompanies} setExtraCompanies={setExtraCompanies}
          extraAdmins={extraAdmins} setExtraAdmins={setExtraAdmins}
          extraUsers={extraUsers} setExtraUsers={setExtraUsers}
          estimate={estimate} usage={usage}
          addonsOpen={addonsOpen} setAddonsOpen={setAddonsOpen}
          checkoutLoading={checkoutLoading} handleCheckout={handleCheckout}
          hasChanges={hasChanges()} openConfirm={() => setConfirmOpen(true)}
        />
      )}

      <ConfirmModifyDialog
        open={confirmOpen} onOpenChange={setConfirmOpen}
        sub={sub} estimate={estimate} stripeDetails={stripeDetails}
        addonChanges={getAddonChanges()}
        modifyLoading={modifyLoading} handleModify={handleModify}
      />

      <InvoicesTable invoices={invoices} />
      <PaymentHistory transactions={transactions} />

      <DemoUpgradePrompt isOpen={showDemoPrompt} onClose={() => setShowDemoPrompt(false)} featureName="Subscription management" />
    </div>
  );
}
